import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Upload, X, ChevronDown, ImageIcon } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useUploadToothImage, IMAGE_KINDS, type ImageKind } from '@/hooks/useToothImages';

// ── All 32 permanent + 20 deciduous FDI codes ─────────────────────────────────

const PERMANENT_FDIS = [
  '11','12','13','14','15','16','17','18',
  '21','22','23','24','25','26','27','28',
  '31','32','33','34','35','36','37','38',
  '41','42','43','44','45','46','47','48',
];

// ── File entry ────────────────────────────────────────────────────────────────

interface FileEntry {
  id: string;
  file: File;
  previewUrl: string;
  kind: ImageKind;
  toothFdis: string[];
  notes: string;
  progress: number; // 0..100
  status: 'pending' | 'uploading' | 'done' | 'error';
  errorMessage?: string;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface ImageUploaderProps {
  clinicId: string;
  patientId: string;
}

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

// ── Component ─────────────────────────────────────────────────────────────────

export function ImageUploader({ clinicId, patientId }: ImageUploaderProps) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useUploadToothImage(clinicId);

  const addFiles = useCallback((files: File[]) => {
    const valid = files.filter((f) => ALLOWED_IMAGE_TYPES.includes(f.type));
    if (valid.length === 0) {
      toast.error(t('dental.imaging.uploader.uploadError', { error: 'invalid_type' }));
      return;
    }

    const newEntries: FileEntry[] = valid.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      kind: 'intraoral_photo',
      toothFdis: [],
      notes: '',
      progress: 0,
      status: 'pending',
    }));

    setEntries((prev) => {
      const combined = [...prev, ...newEntries];

      // Series detection: if 2-4 bitewing images added at once, auto-prompt
      if (
        newEntries.length >= 2 &&
        newEntries.length <= 4 &&
        newEntries.every((e) => e.kind === 'bitewing')
      ) {
        const shouldSeries = confirm(
          t('dental.imaging.uploader.seriesPrompt', { kind: 'Bitewing' }),
        );
        if (shouldSeries) {
          const seriesId = crypto.randomUUID();
          newEntries.forEach((e, i) => {
            e.kind = 'bitewing';
            // Series ID will be used during upload
            (e as FileEntry & { _seriesId?: string; _seriesPosition?: number })._seriesId = seriesId;
            (e as FileEntry & { _seriesId?: string; _seriesPosition?: number })._seriesPosition = i + 1;
          });
        }
      }

      return combined;
    });
  }, [t]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    addFiles(files);
    e.target.value = '';
  };

  const updateEntry = (id: string, patch: Partial<FileEntry>) => {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  };

  const removeEntry = (id: string) => {
    setEntries((prev) => {
      const entry = prev.find((e) => e.id === id);
      if (entry) URL.revokeObjectURL(entry.previewUrl);
      return prev.filter((e) => e.id !== id);
    });
  };

  const handleUpload = async (entry: FileEntry) => {
    updateEntry(entry.id, { status: 'uploading', progress: 0 });
    const ext = entry as FileEntry & { _seriesId?: string; _seriesPosition?: number };

    try {
      await upload.mutateAsync({
        file: entry.file,
        patientId,
        toothFdis: entry.toothFdis,
        imageKind: entry.kind,
        notes: entry.notes || undefined,
        seriesId: ext._seriesId,
        seriesPosition: ext._seriesPosition,
        onProgress: (pct) => updateEntry(entry.id, { progress: pct }),
      });

      updateEntry(entry.id, { status: 'done', progress: 100 });
      toast.success(t('dental.imaging.uploader.uploaded'));

      // Auto-remove after 2s
      setTimeout(() => removeEntry(entry.id), 2000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'unknown';
      updateEntry(entry.id, { status: 'error', errorMessage: msg });
      toast.error(t('dental.imaging.uploader.uploadError', { error: msg }));
    }
  };

  const handleUploadAll = () => {
    entries.filter((e) => e.status === 'pending').forEach(handleUpload);
  };

  return (
    <div className="space-y-3">
      {/* Drop zone */}
      <div
        className={cn(
          'flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 text-center transition-colors cursor-pointer',
          isDragOver
            ? 'border-indigo-400 bg-indigo-50 dark:border-indigo-500 dark:bg-indigo-950/30'
            : 'border-slate-300 bg-white hover:border-indigo-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-indigo-700',
        )}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="mb-2 h-8 w-8 text-slate-400 dark:text-slate-500" />
        <p className="text-sm text-slate-600 dark:text-slate-400">
          {t('dental.imaging.uploader.dropZone')}
        </p>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-600">
          JPEG, PNG, GIF, WebP · max 50 MB
        </p>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ALLOWED_IMAGE_TYPES.join(',')}
          className="hidden"
          onChange={handleFileInput}
        />
      </div>

      {/* File entries */}
      {entries.length > 0 && (
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
          {entries.map((entry) => (
            <FileEntryCard
              key={entry.id}
              entry={entry}
              onUpdate={(patch) => updateEntry(entry.id, patch)}
              onRemove={() => removeEntry(entry.id)}
              onUpload={() => handleUpload(entry)}
              t={t}
            />
          ))}

          {entries.filter((e) => e.status === 'pending').length > 1 && (
            <button
              onClick={handleUploadAll}
              className="w-full rounded-xl bg-indigo-600 py-2 text-sm font-medium text-white hover:bg-indigo-500"
            >
              {t('dental.imaging.uploader.upload')} alle ({entries.filter((e) => e.status === 'pending').length})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── FileEntryCard ─────────────────────────────────────────────────────────────

interface FileEntryCardProps {
  entry: FileEntry;
  onUpdate: (patch: Partial<FileEntry>) => void;
  onRemove: () => void;
  onUpload: () => void;
  t: (key: string, opts?: Record<string, string>) => string;
}

function FileEntryCard({ entry, onUpdate, onRemove, onUpload, t }: FileEntryCardProps) {
  const [showFdiPicker, setShowFdiPicker] = useState(false);

  const toggleFdi = (fdi: string) => {
    const next = entry.toothFdis.includes(fdi)
      ? entry.toothFdis.filter((f) => f !== fdi)
      : [...entry.toothFdis, fdi];
    onUpdate({ toothFdis: next });
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex gap-3">
        {/* Thumbnail */}
        <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-slate-200 dark:bg-slate-700">
          {entry.previewUrl ? (
            <img src={entry.previewUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center">
              <ImageIcon className="h-6 w-6 text-slate-400" />
            </div>
          )}
        </div>

        {/* Fields */}
        <div className="flex flex-1 flex-col gap-2 min-w-0">
          {/* Kind */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 dark:text-slate-400 w-12 flex-shrink-0">
              {t('dental.imaging.uploader.kind')}
            </label>
            <select
              value={entry.kind}
              onChange={(e) => onUpdate({ kind: e.target.value as ImageKind })}
              className="flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            >
              {IMAGE_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`dental.imaging.kinds.${k}`)}
                </option>
              ))}
            </select>
          </div>

          {/* Tooth FDI picker */}
          <div className="flex items-start gap-2">
            <label className="mt-1 text-xs text-slate-500 dark:text-slate-400 w-12 flex-shrink-0">
              {t('dental.imaging.uploader.teeth')}
            </label>
            <div className="flex-1">
              <button
                onClick={() => setShowFdiPicker((v) => !v)}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              >
                <span className="truncate">
                  {entry.toothFdis.length > 0 ? entry.toothFdis.join(', ') : '—'}
                </span>
                <ChevronDown className="h-3 w-3 flex-shrink-0 text-slate-400" />
              </button>

              {showFdiPicker && (
                <div className="mt-1 grid grid-cols-8 gap-0.5 rounded-lg border border-slate-200 bg-white p-2 dark:border-slate-700 dark:bg-slate-900">
                  {PERMANENT_FDIS.map((fdi) => (
                    <button
                      key={fdi}
                      onClick={() => toggleFdi(fdi)}
                      className={cn(
                        'rounded px-0.5 py-0.5 text-xs transition-colors',
                        entry.toothFdis.includes(fdi)
                          ? 'bg-indigo-500 text-white'
                          : 'hover:bg-slate-100 dark:hover:bg-slate-800',
                      )}
                    >
                      {fdi}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500 dark:text-slate-400 w-12 flex-shrink-0">
              {t('dental.imaging.uploader.notes')}
            </label>
            <input
              type="text"
              value={entry.notes}
              onChange={(e) => onUpdate({ notes: e.target.value })}
              placeholder={t('dental.imaging.uploader.notes')}
              className="flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900 dark:text-white"
            />
          </div>
        </div>

        {/* Remove button */}
        <button
          onClick={onRemove}
          className="flex-shrink-0 self-start rounded-lg p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Progress / actions */}
      <div className="mt-2">
        {entry.status === 'uploading' && (
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
            <div
              className="h-full rounded-full bg-indigo-500 transition-all"
              style={{ width: `${entry.progress}%` }}
            />
          </div>
        )}
        {entry.status === 'done' && (
          <p className="text-xs text-emerald-500">{t('dental.imaging.uploader.uploaded')} ✓</p>
        )}
        {entry.status === 'error' && (
          <p className="text-xs text-red-500">
            {t('dental.imaging.uploader.uploadError', { error: entry.errorMessage ?? '' })}
          </p>
        )}
        {entry.status === 'pending' && (
          <button
            onClick={onUpload}
            className="w-full rounded-lg bg-indigo-600 py-1.5 text-xs font-medium text-white hover:bg-indigo-500"
          >
            {t('dental.imaging.uploader.upload')}
          </button>
        )}
      </div>
    </div>
  );
}
